import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LogOut, Mail, Phone, Trash2, Inbox, CheckCircle2, Archive, Filter, RefreshCw, Settings, Users, Lock, UserPlus, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const statusStyles = {
  new: "bg-[#ff3333]/15 text-[#ff3333] border-[#ff3333]/30",
  replied: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  archived: "bg-zinc-700/30 text-zinc-400 border-zinc-700",
};

const statusOptions = ["all", "new", "replied", "archived"];

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, replied: 0, archived: 0 });
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/admin/contacts"), api.get("/admin/stats")]);
      setContacts(c.data);
      setStats(s.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = () => { logout(); navigate("/admin/login"); };

  const updateStatus = async (id, status) => {
    await api.patch(`/admin/contacts/${id}`, { status });
    await load();
    if (selected?.id === id) setSelected({ ...selected, status });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission? This cannot be undone.")) return;
    await api.delete(`/admin/contacts/${id}`);
    if (selected?.id === id) setSelected(null);
    await load();
  };

  const filtered = filter === "all" ? contacts : contacts.filter((c) => c.status === filter);

  const tabs = [
    { id: "inquiries", label: "Inquiries", icon: <Inbox size={14} /> },
    { id: "settings", label: "Settings", icon: <Settings size={14} /> },
  ];

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#050505]" data-testid="admin-dashboard">
      {/* TOP BAR */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <p className="overline text-[#ff3333]">Control Room</p>
            <span className="text-zinc-600 mono text-xs hidden md:block">·</span>
            <span className="text-white font-medium text-sm hidden md:block">{admin?.email}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-xs mono text-zinc-400 hover:text-white uppercase tracking-[0.18em]" data-testid="admin-view-site">View site</Link>
            <button onClick={handleLogout} className="btn-secondary text-xs py-2 px-4" data-testid="admin-logout">
              <LogOut size={14}/> Logout
            </button>
          </div>
        </div>
      </div>

      {/* TAB NAV */}
      <div className="border-b border-white/10 bg-[#0a0a0a]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex gap-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-3 text-xs mono uppercase tracking-[0.16em] border-b-2 transition-colors ${
                tab === t.id
                  ? "border-[#ff3333] text-white"
                  : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-10">
        {tab === "inquiries" ? (
          <InquiriesView
            contacts={contacts}
            stats={stats}
            filter={filter}
            setFilter={setFilter}
            selected={selected}
            setSelected={setSelected}
            loading={loading}
            filtered={filtered}
            load={load}
            updateStatus={updateStatus}
            remove={remove}
          />
        ) : (
          <SettingsView currentEmail={admin?.email} />
        )}
      </div>
    </div>
  );
}

function InquiriesView({ stats, filter, setFilter, selected, setSelected, loading, filtered, load, updateStatus, remove }) {
  return (
    <>
      <div className="flex items-end justify-between gap-6 mb-10">
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-black text-white tracking-tight leading-tight" data-testid="admin-title">
            Inquiries
          </h1>
          <p className="mt-2 text-sm text-zinc-400">All contact form submissions. Newest first.</p>
        </div>
        <button onClick={load} className="btn-secondary text-xs py-2 px-4" data-testid="admin-refresh">
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <StatCard icon={<Inbox size={18}/>} label="Total" value={stats.total} testid="stat-total" />
        <StatCard icon={<Mail size={18}/>} label="New" value={stats.new} accent testid="stat-new" />
        <StatCard icon={<CheckCircle2 size={18}/>} label="Replied" value={stats.replied} testid="stat-replied" />
        <StatCard icon={<Archive size={18}/>} label="Archived" value={stats.archived} testid="stat-archived" />
      </div>

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <Filter size={14} className="text-zinc-500"/>
        {statusOptions.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-xs mono uppercase tracking-[0.16em] border transition-colors ${
              filter === s ? "bg-white text-black border-white" : "text-zinc-400 border-white/10 hover:border-white/30 hover:text-white"
            }`}
            data-testid={`admin-filter-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-7 border border-white/10 bg-[#0a0a0a] overflow-hidden">
          {loading ? (
            <div className="p-16 text-center text-zinc-500 mono text-xs">LOADING…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox size={40} className="mx-auto text-zinc-700 mb-4"/>
              <p className="text-zinc-400">No submissions {filter !== "all" && `in "${filter}"`} yet.</p>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  data-testid={`admin-contact-row-${c.id}`}
                  className={`w-full text-left px-5 py-4 hover:bg-[#111] transition-colors ${
                    selected?.id === c.id ? "bg-[#111] border-l-2 border-[#ff3333]" : "border-l-2 border-transparent"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-white font-semibold truncate">{c.name}</span>
                        <span className={`text-[10px] mono uppercase tracking-[0.14em] border px-1.5 py-0.5 ${statusStyles[c.status]}`}>{c.status}</span>
                      </div>
                      <div className="text-xs text-zinc-500 mono mt-0.5 truncate">{c.email}</div>
                      <div className="mt-2 text-sm text-zinc-300 truncate">{c.subject}</div>
                    </div>
                    <div className="text-[10px] mono text-zinc-600 shrink-0">
                      {new Date(c.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5">
          <AnimatePresence mode="wait">
            {selected ? (
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8 sticky top-24"
                data-testid="admin-contact-detail"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <span className={`text-[10px] mono uppercase tracking-[0.14em] border px-2 py-0.5 ${statusStyles[selected.status]}`}>{selected.status}</span>
                    <h3 className="mt-3 font-display text-2xl font-bold text-white">{selected.name}</h3>
                    <p className="text-xs mono text-zinc-500 mt-1">{new Date(selected.created_at).toLocaleString()}</p>
                  </div>
                  <button onClick={() => remove(selected.id)} className="p-2 text-zinc-500 hover:text-[#ff3333] transition-colors" data-testid="admin-delete-contact" aria-label="Delete">
                    <Trash2 size={16}/>
                  </button>
                </div>

                <div className="mt-6 space-y-3">
                  <a href={`mailto:${selected.email}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#ff3333]">
                    <Mail size={14} className="text-[#ff3333]"/> {selected.email}
                  </a>
                  {selected.phone && (
                    <a href={`tel:${selected.phone}`} className="flex items-center gap-2 text-sm text-zinc-300 hover:text-[#ff3333]">
                      <Phone size={14} className="text-[#ff3333]"/> {selected.phone}
                    </a>
                  )}
                </div>

                <div className="mt-6 border-t border-white/5 pt-5">
                  <p className="overline text-zinc-500">Subject</p>
                  <p className="mt-2 text-white font-semibold">{selected.subject}</p>
                </div>
                <div className="mt-5">
                  <p className="overline text-zinc-500">Message</p>
                  <p className="mt-2 text-zinc-300 whitespace-pre-wrap leading-relaxed">{selected.message}</p>
                </div>

                <div className="mt-8 flex flex-wrap gap-2 border-t border-white/5 pt-5">
                  <button onClick={() => updateStatus(selected.id, "new")} disabled={selected.status === "new"} className="btn-secondary text-xs py-2 px-3 disabled:opacity-40" data-testid="admin-mark-new">Mark New</button>
                  <button onClick={() => updateStatus(selected.id, "replied")} disabled={selected.status === "replied"} className="btn-primary text-xs py-2 px-3 disabled:opacity-40" data-testid="admin-mark-replied">Mark Replied</button>
                  <button onClick={() => updateStatus(selected.id, "archived")} disabled={selected.status === "archived"} className="btn-secondary text-xs py-2 px-3 disabled:opacity-40" data-testid="admin-mark-archived">Archive</button>
                </div>
              </motion.div>
            ) : (
              <div className="border border-dashed border-white/10 bg-[#0a0a0a] p-10 text-center text-zinc-500" data-testid="admin-empty-detail">
                <Inbox size={36} className="mx-auto text-zinc-700 mb-4"/>
                <p className="text-sm">Select a submission to view details.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}

function SettingsView({ currentEmail }) {
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);

  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState(null);

  const [newAdmin, setNewAdmin] = useState({ email: "", password: "", confirm_password: "" });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState(null);

  const loadAdmins = async () => {
    setLoadingAdmins(true);
    try {
      const r = await api.get("/admin/users");
      setAdmins(r.data);
    } catch { /* ignore */ } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => { loadAdmins(); }, []);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwError(null);
    if (pwForm.new_password !== pwForm.confirm_password) {
      setPwError("New passwords do not match.");
      return;
    }
    if (pwForm.new_password.length < 8) {
      setPwError("New password must be at least 8 characters.");
      return;
    }
    setPwLoading(true);
    try {
      await api.patch("/admin/change-password", {
        current_password: pwForm.current_password,
        new_password: pwForm.new_password,
      });
      toast.success("Password changed successfully.");
      setPwForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (err) {
      setPwError(err.response?.data?.detail || "Failed to change password.");
    } finally {
      setPwLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setCreateError(null);
    if (newAdmin.password !== newAdmin.confirm_password) {
      setCreateError("Passwords do not match.");
      return;
    }
    if (newAdmin.password.length < 8) {
      setCreateError("Password must be at least 8 characters.");
      return;
    }
    setCreateLoading(true);
    try {
      await api.post("/admin/users", {
        email: newAdmin.email,
        password: newAdmin.password,
      });
      toast.success(`Admin ${newAdmin.email} created.`);
      setNewAdmin({ email: "", password: "", confirm_password: "" });
      await loadAdmins();
    } catch (err) {
      setCreateError(err.response?.data?.detail || "Failed to create admin.");
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <>
      <div className="mb-10">
        <h1 className="font-display text-4xl md:text-5xl font-black text-white tracking-tight leading-tight">
          Settings
        </h1>
        <p className="mt-2 text-sm text-zinc-400">Manage admin accounts and security.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* CHANGE PASSWORD */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8">
          <div className="flex items-center gap-3 text-[#ff3333] mb-6">
            <Lock size={18} />
            <span className="overline text-[#ff3333]">Change Password</span>
          </div>
          <p className="text-sm text-zinc-400 mb-6">Update the password for <span className="text-white font-medium">{currentEmail}</span></p>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <label className="block">
              <span className="overline block mb-2">Current Password <span className="text-[#ff3333]">*</span></span>
              <input
                type="password"
                required
                value={pwForm.current_password}
                onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                className="ridge-input"
                placeholder="Enter current password"
              />
            </label>
            <label className="block">
              <span className="overline block mb-2">New Password <span className="text-[#ff3333]">*</span></span>
              <input
                type="password"
                required
                minLength={8}
                value={pwForm.new_password}
                onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                className="ridge-input"
                placeholder="Min. 8 characters"
              />
            </label>
            <label className="block">
              <span className="overline block mb-2">Confirm New Password <span className="text-[#ff3333]">*</span></span>
              <input
                type="password"
                required
                minLength={8}
                value={pwForm.confirm_password}
                onChange={(e) => setPwForm({ ...pwForm, confirm_password: e.target.value })}
                className="ridge-input"
                placeholder="Re-enter new password"
              />
            </label>
            {pwError && (
              <div className="border border-red-500/30 bg-red-500/10 text-red-300 p-3 flex items-start gap-2 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{pwError}</span>
              </div>
            )}
            <button type="submit" disabled={pwLoading} className="btn-primary text-sm disabled:opacity-60">
              {pwLoading ? "Updating…" : "Update Password"}
            </button>
          </form>
        </div>

        {/* CREATE ADMIN */}
        <div className="border border-white/10 bg-[#0a0a0a] p-6 md:p-8">
          <div className="flex items-center gap-3 text-[#ff3333] mb-6">
            <UserPlus size={18} />
            <span className="overline text-[#ff3333]">Create Admin User</span>
          </div>
          <p className="text-sm text-zinc-400 mb-6">Add a new administrator to the control room.</p>
          <form onSubmit={handleCreateAdmin} className="space-y-4">
            <label className="block">
              <span className="overline block mb-2">Email <span className="text-[#ff3333]">*</span></span>
              <input
                type="email"
                required
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                className="ridge-input"
                placeholder="admin@ridgecx.com"
              />
            </label>
            <label className="block">
              <span className="overline block mb-2">Password <span className="text-[#ff3333]">*</span></span>
              <input
                type="password"
                required
                minLength={8}
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                className="ridge-input"
                placeholder="Min. 8 characters"
              />
            </label>
            <label className="block">
              <span className="overline block mb-2">Confirm Password <span className="text-[#ff3333]">*</span></span>
              <input
                type="password"
                required
                minLength={8}
                value={newAdmin.confirm_password}
                onChange={(e) => setNewAdmin({ ...newAdmin, confirm_password: e.target.value })}
                className="ridge-input"
                placeholder="Re-enter password"
              />
            </label>
            {createError && (
              <div className="border border-red-500/30 bg-red-500/10 text-red-300 p-3 flex items-start gap-2 text-sm">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{createError}</span>
              </div>
            )}
            <button type="submit" disabled={createLoading} className="btn-primary text-sm disabled:opacity-60">
              {createLoading ? "Creating…" : "Create Admin"}
            </button>
          </form>
        </div>
      </div>

      {/* ADMIN LIST */}
      <div className="mt-10 border border-white/10 bg-[#0a0a0a] p-6 md:p-8">
        <div className="flex items-center gap-3 text-[#ff3333] mb-6">
          <Users size={18} />
          <span className="overline text-[#ff3333]">Admin Users</span>
        </div>
        {loadingAdmins ? (
          <div className="text-zinc-500 mono text-xs py-8 text-center">LOADING…</div>
        ) : admins.length === 0 ? (
          <p className="text-zinc-400 text-sm">No admin users found.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-4 overline text-zinc-500">Email</th>
                  <th className="text-left py-3 px-4 overline text-zinc-500">Status</th>
                  <th className="text-left py-3 px-4 overline text-zinc-500">Last Login</th>
                  <th className="text-left py-3 px-4 overline text-zinc-500">Created</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.id || a.email} className="border-b border-white/5 hover:bg-[#111] transition-colors">
                    <td className="py-3 px-4 text-white font-medium">
                      {a.email}
                      {a.email === currentEmail && (
                        <span className="ml-2 text-[10px] mono uppercase tracking-[0.14em] border border-[#ff3333]/30 bg-[#ff3333]/15 text-[#ff3333] px-1.5 py-0.5">You</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`text-[10px] mono uppercase tracking-[0.14em] border px-1.5 py-0.5 ${
                        a.is_active ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"
                      }`}>
                        {a.is_active ? "Active" : "Disabled"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-zinc-400 mono text-xs">
                      {a.last_login ? new Date(a.last_login).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "Never"}
                    </td>
                    <td className="py-3 px-4 text-zinc-400 mono text-xs">
                      {a.created_at ? new Date(a.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <style>{`
        .ridge-input {
          width: 100%;
          background: #050505;
          border: 1px solid #27272a;
          color: #fff;
          padding: 12px 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color .2s ease;
          border-radius: 0;
        }
        .ridge-input:focus { border-color: #ff3333; }
        .ridge-input::placeholder { color: #52525b; }
      `}</style>
    </>
  );
}

const StatCard = ({ icon, label, value, accent = false, testid }) => (
  <div
    className={`border p-5 bg-[#0a0a0a] ${accent ? "border-[#ff3333]/40" : "border-white/10"}`}
    data-testid={testid}
  >
    <div className={`flex items-center gap-2 ${accent ? "text-[#ff3333]" : "text-zinc-500"}`}>
      {icon}<span className="overline" style={accent ? { color: "#ff3333" } : {}}>{label}</span>
    </div>
    <div className="mt-3 font-display text-4xl font-black text-white tracking-tight">{value}</div>
  </div>
);
